import {
	type ProductOption,
	type Product,
	type ProductImage,
	type ProductOptionValue,
	type ProductVariant,
} from "@prisma/client"
import { Link, useFetcher } from "react-router"
import { useState } from "react"
import LoaderImage from "#app/components/loader-image"
import { ShareButtons } from "#app/components/share-buttons"
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "#app/components/ui/accordion"
import { Button } from "#app/components/ui/button"
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "#app/components/ui/drawer"
import { Separator } from "#app/components/ui/separator"
import {
	TypographyH1,
	TypographyLarge,
	TypographyP,
} from "#app/components/ui/typography"
import { addToCartIntent } from "#app/routes/_marketing+/cart"
import { getProductImgSrc, price } from "#app/utils/misc"
import { productFaqs } from "#app/utils/product-faqs"
import { useOptionalUser, userHasRole } from "#app/utils/user"
import routes from "#routes"
import { getAltText, type SupportedLocale } from "#app/utils/alt-text"
import { WishlistButton } from "#app/components/wishlist/wishlist-button"
import { Icon } from "#app/components/ui/icon"

type ProductImageWithTranslations = Pick<ProductImage, "id" | "altText"> & {
	altTextTranslations: string | null
}

function getImageAltText(
	image: ProductImageWithTranslations,
	locale: SupportedLocale = "es",
): string {
	return getAltText(image.altTextTranslations, image.altText || "", locale)
}

const AddToCart = ({
	product,
	amountInCart,
}: {
	product: Product & {
		options: (ProductOption & { values: ProductOptionValue[] })[]
		variants: (ProductVariant & { options: ProductOptionValue[] })[]
		images: ProductImageWithTranslations[]
	}
	amountInCart?: number
}) => {
	const fetcher = useFetcher()
	const [variantId, setVariantId] = useState<string | undefined>()

	if (product.variants.length === 0) {
		return (
			<fetcher.Form action="/cart" method="POST">
				<input type="hidden" name="productId" value={product.id} />
				<Button
					className="w-full"
					disabled={product.stock === 0 || amountInCart === 1}
					name="intent"
					value={addToCartIntent}
					type="submit"
				>
					{amountInCart === 1
						? "Producto en tu carrito"
						: fetcher.state === "idle"
							? "Añadir al carrito"
							: "Añadiendo al carrito..."}
				</Button>
			</fetcher.Form>
		)
	}

	return (
		<Drawer>
			<DrawerTrigger asChild>
				<Button type="button" className="w-full">
					Añadir al carrito
				</Button>
			</DrawerTrigger>
			<DrawerContent className="flex items-center justify-center">
				<fetcher.Form
					className="w-full max-w-[700px]"
					action="/cart"
					method="POST"
				>
					<DrawerHeader>
						<DrawerTitle>Selecciona la variante</DrawerTitle>
						<DrawerDescription>
							Selecciona las opciones para añadir el producto al carrito.
						</DrawerDescription>
					</DrawerHeader>
					<div className="mx-4 my-5 grid grid-cols-1 gap-4">
						<input type="hidden" name="productId" value={product.id} />
						<div className="flex flex-col gap-2">
							{product.variants.map(variant => (
								<label
									key={variant.id}
									className="flex flex-1 cursor-pointer select-none items-center gap-2"
								>
									<input
										disabled={variant.stock === 0}
										type="radio"
										className="peer absolute h-0 w-0 cursor-pointer opacity-0"
										name="variantId"
										value={variant.id}
										onChange={() => {
											setVariantId(variant.id)
										}}
									/>
									<span className="w-full rounded-md border px-3 py-2 text-center transition-colors duration-200 ease-in-out peer-checked:bg-secondary peer-disabled:cursor-not-allowed peer-disabled:opacity-30">
										{variant.title}
									</span>
								</label>
							))}
						</div>

						<Separator />

						<div className="text-end">
							<TypographyLarge>
								{price(
									product.variants.find(variant => variant.id === variantId)
										?.price || product.price,
								)}
							</TypographyLarge>
						</div>
					</div>
					<DrawerFooter className="flex w-full flex-col gap-2  lg:flex-row-reverse">
						<Button
							disabled={!variantId}
							name="intent"
							value={addToCartIntent}
							type="submit"
							className="w-full flex-1"
						>
							Añadir al carrito
						</Button>
						<DrawerClose asChild>
							<Button variant="outline" className="w-full flex-1" type="button">
								Cancel
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</fetcher.Form>
			</DrawerContent>
		</Drawer>
	)
}

export default function ProductDetail({
	showActions = false,
	amountInCart,
	...product
}: Product & {
	options: (ProductOption & { values: ProductOptionValue[] })[]
	variants: (ProductVariant & {
		options: ProductOptionValue[]
	})[]
	images: ProductImageWithTranslations[]
} & (
		| { showActions?: true; amountInCart: number }
		| { showActions?: false; amountInCart?: never }
	)) {
	const user = useOptionalUser()
	const isAdmin = userHasRole(user || null, "admin")

	return (
		<div className="relative grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(500px,_1fr)_1fr]">
			<div className="top-12 flex flex-col lg:sticky">
				<TypographyH1>{product.name}</TypographyH1>
				<TypographyP className="text-2xl">
					{product.variants.length > 0 && (
						<span className="block text-xs font-bold">desde</span>
					)}
					{product.variants.length === 0
						? price(product.price)
						: // Take the minimum price of the variants available
							price(
								product.variants.reduce(
									(min, variant) => (variant.price < min ? variant.price : min),
									product.price,
								),
							)}
				</TypographyP>
				{showActions && (
					<div className="mt-6 grid grid-cols-1 items-center gap-2 md:grid-cols-2">
						<AddToCart amountInCart={amountInCart} product={product} />

						<WishlistButton productId={product.id} variant="button" />

						{isAdmin && (
							<Link to={routes.admin.product.edit.route(product.id)}>
								<Button className="w-full" variant="outline">
									Editar producto
								</Button>
							</Link>
						)}
					</div>
				)}
				<article
					dangerouslySetInnerHTML={{
						__html: product.description,
					}}
					className="prose mt-6 whitespace-pre-line dark:text-gray-200"
				/>
				{showActions && (
					<div className="mt-6">
						<ShareButtons
							url={routes.public.product.route(product.handle)}
							title={product.name}
							description={product.description
								.replace(/<[^>]*>/g, "")
								.slice(0, 160)}
						/>
					</div>
				)}
				{showActions && (
					<div className="mt-8">
						<h2 className="mb-2 text-lg font-semibold">Preguntas frecuentes</h2>
						<Accordion type="single" collapsible>
							{productFaqs.map((faq, index) => (
								<AccordionItem key={index} value={`faq-${index}`}>
									<AccordionTrigger>{faq.question}</AccordionTrigger>
									<AccordionContent className="text-muted-foreground">
										{faq.answer}
									</AccordionContent>
								</AccordionItem>
							))}
						</Accordion>
					</div>
				)}
			</div>

			<div className="flex flex-col gap-3">
				{product.isUnique && (
					<div className="flex w-full items-center justify-center gap-2 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 dark:border-amber-800 dark:from-amber-950 dark:to-orange-950">
						<Icon
							name="lucide-star"
							className="h-4 w-4 text-amber-600 dark:text-amber-400"
						/>
						<span className="text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-200">
							Pieza única
						</span>
						<span className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
							— Hecha a mano, única en el mundo
						</span>
					</div>
				)}
				{product.images.map((image, index) => (
					<LoaderImage
						key={index}
						alt={getImageAltText(image)}
						src={getProductImgSrc(image.id)}
						loading={index === 0 ? "eager" : "lazy"}
						sizes="(max-width: 768px) 100vw, 50vw"
					/>
				))}
			</div>
		</div>
	)
}
